"use client";
// SubAgreementTemplatesManagement.jsx
// Company Profile → standard scope templates + default payment terms /
// conditions of work used to pre-fill every new subcontractor agreement.
import { useState, useEffect } from "react";
import axios from "axios";

const BASE =
  "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile";

const inputCls =
  "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
const labelCls =
  "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

export default function SubAgreementTemplatesManagement({ setDialog }) {
  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  const showErr = (err, fallback) =>
    setDialog({
      open: true,
      type: "error",
      title: "Error",
      message: err?.response?.data?.message || fallback,
      onConfirm: () => setDialog((p) => ({ ...p, open: false })),
    });

  // ── Scope templates ──
  const [templates, setTemplates] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", scopeText: "" });

  // ── Default text ──
  const [settings, setSettings] = useState({
    scopeIntro: "",
    paymentTerms: "",
    conditionsOfWork: "",
    contractorPhone: "",
    contractorEmail: "",
  });
  const [defaults, setDefaults] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const fetchTemplates = async () => {
    try {
      const r = await axios.get(`${BASE}/scope-templates`, auth());
      setTemplates(r.data || []);
    } catch {}
  };
  const fetchSettings = async () => {
    try {
      const [s, d] = await Promise.all([
        axios.get(`${BASE}/sub-agreement-settings`, auth()),
        axios.get(`${BASE}/sub-agreement-defaults`, auth()),
      ]);
      setSettings({ ...settings, ...s.data });
      setDefaults(d.data);
    } catch {}
  };

  useEffect(() => {
    fetchTemplates();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm({ name: "", scopeText: "" });
  };

  const saveTemplate = async () => {
    if (!form.name.trim() || !form.scopeText.trim()) return;
    try {
      if (editingId)
        await axios.put(`${BASE}/scope-templates/${editingId}`, form, auth());
      else await axios.post(`${BASE}/scope-templates`, form, auth());
      resetForm();
      fetchTemplates();
    } catch (err) {
      showErr(err, "Failed to save template.");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this scope template?")) return;
    try {
      await axios.delete(`${BASE}/scope-templates/${id}`, auth());
      fetchTemplates();
    } catch (err) {
      showErr(err, "Failed to delete template.");
    }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(
        `${BASE}/scope-templates/${t._id}`,
        { active: !t.active },
        auth(),
      );
      fetchTemplates();
    } catch {}
  };

  const addStarter = () => {
    if (!defaults?.starterScopeTemplate) return;
    setForm({ ...defaults.starterScopeTemplate });
    setEditingId(null);
    setIsAdding(true);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${BASE}/sub-agreement-settings`, settings, auth());
      setDialog({
        open: true,
        type: "success",
        title: "Saved",
        message: "Agreement defaults updated. New agreements will use them.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    } catch (err) {
      showErr(err, "Failed to save.");
    } finally {
      setSavingSettings(false);
    }
  };

  const resetField = (key) => {
    if (!defaults) return;
    if (!window.confirm("Reset this text to the built-in default?")) return;
    setSettings((s) => ({ ...s, [key]: defaults[key] }));
  };

  return (
    <div className="mt-8 border-t border-gray-700 pt-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">
            Subcontractor Agreement Templates
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Standard scopes of work to import into agreements, plus default
            payment terms and conditions of work
          </p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && defaults?.starterScopeTemplate && (
            <button
              onClick={addStarter}
              className="text-xs text-orange-400 hover:text-orange-300"
            >
              Start from example scope
            </button>
          )}
          <button
            onClick={() => {
              setIsAdding(true);
              setEditingId(null);
              setForm({ name: "", scopeText: "" });
            }}
            className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            + Add Scope Template
          </button>
        </div>
      </div>

      {isAdding && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-4">
          <p className="text-xs font-semibold text-orange-400 uppercase tracking-widest mb-4">
            {editingId ? "Edit scope template" : "New scope template"}
          </p>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Template Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g. Drywall Taping – Level 4"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>
                Scope of Work{" "}
                <span className="normal-case font-normal text-gray-600">
                  — one item per line · “- ” = bullet · “** ” = bold note
                </span>
              </label>
              <textarea
                rows={12}
                value={form.scopeText}
                onChange={(e) =>
                  setForm((p) => ({ ...p, scopeText: e.target.value }))
                }
                className={`${inputCls} font-mono`}
                placeholder={
                  "Taping of all drywall joints\nPre-fill all drywall gaps…\n** Jobsite shall be clean and tidy at all times."
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveTemplate}
              className="px-5 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
            >
              {editingId ? "Save Changes" : "Save Template"}
            </button>
          </div>
        </div>
      )}

      {templates.length > 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Template</th>
                <th className="text-left px-4 py-3 font-semibold">Preview</th>
                <th className="text-left px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {templates.map((t, i) => (
                <tr
                  key={t._id}
                  className={`${i < templates.length - 1 ? "border-b border-gray-700/50" : ""} ${!t.active ? "opacity-50" : ""}`}
                >
                  <td className="px-5 py-3.5 font-medium text-gray-100">
                    {t.name}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-gray-500 max-w-md truncate">
                    {t.scopeText.replace(/\n/g, " · ")}
                  </td>
                  <td className="px-4 py-3.5">
                    <button
                      onClick={() => toggleActive(t)}
                      className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${t.active ? "bg-orange-400" : "bg-gray-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${t.active ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => {
                          setEditingId(t._id);
                          setForm({ name: t.name, scopeText: t.scopeText });
                          setIsAdding(true);
                        }}
                        className="text-xs text-orange-300 hover:text-orange-200 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTemplate(t._id)}
                        className="text-xs text-gray-500 hover:text-red-400 font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-8 text-center">
          <p className="text-sm text-gray-500">No scope templates yet.</p>
        </div>
      )}

      {/* ── Default text ── */}
      <div className="mt-6 bg-gray-800 border border-gray-700 rounded-xl">
        <button
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <p className="text-sm font-semibold text-white">
              Agreement Defaults
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Payment terms, conditions of work, contractor contact — copied
              into each new agreement, editable per agreement
            </p>
          </div>
          <span className="text-xs text-gray-500">
            {settingsOpen ? "▲ Hide" : "▼ Edit"}
          </span>
        </button>

        {settingsOpen && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Contractor Phone</label>
                <input
                  type="text"
                  value={settings.contractorPhone}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      contractorPhone: e.target.value,
                    }))
                  }
                  placeholder="(416) 628-1972"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Contractor Email</label>
                <input
                  type="email"
                  value={settings.contractorEmail}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      contractorEmail: e.target.value,
                    }))
                  }
                  placeholder="info@torontotapers.ca"
                  className={inputCls}
                />
              </div>
            </div>

            {[
              ["scopeIntro", "Scope of Work Intro Sentence", 2],
              ["paymentTerms", "Payment Terms", 10],
              ["conditionsOfWork", "Conditions of Work", 18],
            ].map(([key, label, rows]) => (
              <div key={key}>
                <div className="flex items-center justify-between">
                  <label className={labelCls}>{label}</label>
                  <button
                    onClick={() => resetField(key)}
                    className="text-[11px] text-gray-500 hover:text-orange-300"
                  >
                    Reset to built-in default
                  </button>
                </div>
                <textarea
                  rows={rows}
                  value={settings[key]}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, [key]: e.target.value }))
                  }
                  className={`${inputCls} font-mono text-xs`}
                />
              </div>
            ))}
            <p className="text-[11px] text-gray-500">
              One item per line (numbered automatically). “- ” starts a bullet
              under the previous item; “** ” is a bold un-numbered note.
            </p>
            <div className="flex justify-end">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="px-5 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm disabled:opacity-40"
              >
                {savingSettings ? "Saving…" : "Save Defaults"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── In CompanyProfileManagement.jsx ─────────────────────────────────────────
//   import SubAgreementTemplatesManagement from "./SubAgreementTemplatesManagement";
//   …
//   <SafetyDocTypesManagement setDialog={setDialog} />
//   <CostCodesManagement setDialog={setDialog} />
//   <SubAgreementTemplatesManagement setDialog={setDialog} />   {/* ← add */}
